/** Copyright 2023 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import {
  ApiService,
  IOverviewStatisticsWasteByPerson,
  IOverviewStatisticsNumberOfWaste,
  IOverviewStatisticsWasteByDrug,
  OverviewStatisticsNumberOfWitness,
  IOverviewStatisticsNumberOfWasteByHospital,
  IOverviewStatisticsNumberOfWitnessByHospital,
  IOverviewStatisticsRxInterceptWaste,
  IOverviewStatisticsNumberOfStandardWaste,
  IOverviewStadisticsStandardWasteLocation,
  IOverviewStatisticsRxInterceptWasteByDateRange,
} from '@midas/shared/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { map } from 'rxjs';
import * as OverviewStatisticsActions from './overview-statistics.actions';

@Injectable()
export class OverviewStatisticsEffects {
  readonly queryWasteByPerson$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryWasteByPerson),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; waste: IOverviewStatisticsWasteByPerson[] }>(
              '/overview/statistics/waste-by-person/',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryWasteByPersonSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryWasteByPersonError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfWaste$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryNumberOfWaste),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<IOverviewStatisticsNumberOfWaste>('/overview/statistics/number-of-waste', {
              startDate,
              endDate,
            })
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryNumberOfWasteSuccess({ data: res });
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryNumberOfWasteError({ error });
        },
      }),
    );
  });

  readonly queryWasteByDrug$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryWasteByDrug),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; waste: IOverviewStatisticsWasteByDrug[] }>(
              '/overview/statistics/waste-by-drug',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryWasteByDrugSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryWasteByDrugError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfWitness$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryNumberOfWitness),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<OverviewStatisticsNumberOfWitness>('/overview/statistics/number-of-anytime-witness', {
              startDate,
              endDate,
            })
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryNumberOfWitnessSuccess({ data: res });
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryNumberOfWitnessError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfWasteByHospital$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryNumberOfWasteByHospital),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; items: IOverviewStatisticsNumberOfWasteByHospital[] }>(
              '/overview/statistics/hospital-waste',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryNumberOfWasteByHopitalSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryNumberOfWasteByHospitalError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfWitnessByHospital$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryNumberOfWitnessByHospital),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; items: IOverviewStatisticsNumberOfWitnessByHospital[] }>(
              '/overview/statistics/number-of-anytime-witness-location',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryNumberOfWitnessByHospitalSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryNumberOfWitnessByHospitalError({ error });
        },
      }),
    );
  });

  readonly queryRxInterceptWaste$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryRxInterceptWaste),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; items: IOverviewStatisticsRxInterceptWaste[] }>(
              '/overview/statistics/number-of-rxintercept-waste-location',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryRxInterceptWasteSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryRxInterceptWasteError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfStandardWaste$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryNumberOfStandardWaste),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<IOverviewStatisticsNumberOfStandardWaste>('/overview/statistics/standard-waste', {
              startDate: startDate,
              endDate: endDate,
            })
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryNumberOfStandardWasteSuccess({ data: res });
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryNumberOfStandardWasteError({ error });
        },
      }),
    );
  });

  readonly queryNumberOfStandardWasteByDateRange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryRxInterceptWasteByDateRange),
      fetch({
        run: ({ startDate, endDate }) => {
          const query = { startDate, endDate };
          return this.api
            .get<IOverviewStatisticsRxInterceptWasteByDateRange>(
              '/overview/statistics/number-of-rxintercept-waste',
              { ...query },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryRxInterceptWasteByDateRangeSuccess({ data: res });
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryRxInterceptWasteByDateRangeError({ error });
        },
      }),
    );
  });

  readonly queryStandardWasteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewStatisticsActions.queryStandardWasteLocation),
      fetch({
        run: ({ startDate, endDate }) => {
          return this.api
            .get<{ count: number; items: IOverviewStadisticsStandardWasteLocation[] }>(
              '/overview/statistics/standard-waste-location',
              { startDate, endDate },
            )
            .pipe(
              map((res) => {
                return OverviewStatisticsActions.queryStandardWasteLocationSuccess(res);
              }),
            );
        },
        onError: (action, error) => {
          return OverviewStatisticsActions.queryStandardWasteLocationError({ error });
        },
      }),
    );
  });

  constructor(private actions$: Actions, private api: ApiService) {}
}
